export const supportedImageTypes = [
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

interface ImageBitmapLike {
  width: number;
  height: number;
  close(): void;
}

interface CanvasContextLike {
  drawImage(
    image: ImageBitmapLike,
    dx: number,
    dy: number,
    width: number,
    height: number,
  ): void;
}

interface CanvasLike {
  width: number;
  height: number;
  getContext(type: '2d'): CanvasContextLike | null;
  toBlob(
    callback: (blob: Blob | null) => void,
    type: string,
    quality: number,
  ): void;
}

interface ImagePreparationDependencies {
  createBitmap?: (file: File) => Promise<ImageBitmapLike>;
  createCanvas?: () => CanvasLike;
}

export interface PreparedImageUpload {
  file: File;
  thumbnail: Blob;
  width: number;
  height: number;
  thumbnailWidth: number;
  thumbnailHeight: number;
}

const browserCreateBitmap = (file: File) => createImageBitmap(file);
const browserCreateCanvas = () => document.createElement('canvas');

const canvasBlob = (canvas: CanvasLike) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Image thumbnail could not be created.'));
      },
      'image/jpeg',
      0.7,
    );
  });

export const prepareImageUpload = async (
  file: File,
  uploadLimit?: number,
  dependencies: ImagePreparationDependencies = {},
): Promise<PreparedImageUpload> => {
  if (
    !supportedImageTypes.includes(
      file.type as (typeof supportedImageTypes)[number],
    )
  )
    throw new Error('Unsupported image type.');
  if (uploadLimit != null && file.size > uploadLimit)
    throw new Error('Image exceeds the upload limit.');

  const bitmap = await (dependencies.createBitmap ?? browserCreateBitmap)(file);
  try {
    const longestSide = Math.max(bitmap.width, bitmap.height);
    const scale = longestSide > 320 ? 320 / longestSide : 1;
    const thumbnailWidth = Math.max(1, Math.round(bitmap.width * scale));
    const thumbnailHeight = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = (dependencies.createCanvas ?? browserCreateCanvas)();
    canvas.width = thumbnailWidth;
    canvas.height = thumbnailHeight;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Image thumbnail could not be created.');
    context.drawImage(bitmap, 0, 0, thumbnailWidth, thumbnailHeight);
    const thumbnail = await canvasBlob(canvas);
    return {
      file,
      thumbnail,
      width: bitmap.width,
      height: bitmap.height,
      thumbnailWidth,
      thumbnailHeight,
    };
  } finally {
    bitmap.close();
  }
};

export const buildVoiceWaveform = (samples: readonly number[], length = 30) => {
  if (length <= 0) return [];
  if (samples.length === 0) return Array.from({ length }, () => 0);
  const sampled = Array.from({ length }, (_, index) => {
    const sourceIndex = Math.min(
      samples.length - 1,
      Math.floor((index * samples.length) / length),
    );
    return Math.max(0, Math.min(1, Math.abs(samples[sourceIndex] ?? 0)));
  });
  const peak = Math.max(...sampled);
  if (peak === 0) return sampled.map(() => 0);
  return sampled.map((sample) => Math.round((sample / peak) * 1024));
};

interface ObjectUrlDependencies {
  createObjectURL(blob: Blob): string;
  revokeObjectURL(url: string): void;
}

const browserObjectUrls: ObjectUrlDependencies = {
  createObjectURL: (blob) => URL.createObjectURL(blob),
  revokeObjectURL: (url) => URL.revokeObjectURL(url),
};

export const createAuthenticatedMediaObjectUrl = async (
  fetchBlob: (mxc: string) => Promise<Blob>,
  mxc: string,
  dependencies: ObjectUrlDependencies = browserObjectUrls,
) => {
  const blob = await fetchBlob(mxc);
  const url = dependencies.createObjectURL(blob);
  let revoked = false;
  return {
    url,
    revoke: () => {
      if (revoked) return;
      revoked = true;
      dependencies.revokeObjectURL(url);
    },
  };
};
